import React, { ChangeEvent, FC, useRef, useState } from 'react';

import { Api } from '@.api';
import { ModalInfo } from '@.modal';
import { Button } from '@/Components/UI';
import { ConsoleDev } from '@/Lib/ConsoleDev';
import { Chamber } from '@/Services/IRomaStationWS';

import { stationDetailStoreSelectors } from '../../../../../model/StationDetailStore';
import { ChamberCardEdit } from '../ChamberCardEdit';
import S from './ChambersModalEdit.module.scss';

interface ChambersModalEditProps {
    deviceId: number;
    initialChambers: Chamber[];
    onClose: () => void;
}

export const ChambersModalEdit: FC<ChambersModalEditProps> = ({ initialChambers, onClose, deviceId }) => {
    const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

    const [error, setError] = useState<string | null>(null);

    const [chambers, setChambers] = useState<Chamber[]>(initialChambers);

    const inputRef = useRef<HTMLInputElement>(null);

    const saveChambers = stationDetailStoreSelectors.use.saveChambers();

    const updateChamber = (chamberIndex: number, key: keyof Chamber, value: string | number | boolean | object) => {
        setChambers((chambers) => {
            return chambers.map((chamber) => {
                if (chamber.index === chamberIndex) {
                    return {
                        ...chamber,
                        [key]: value,
                    };
                }

                return chamber;
            });
        });
    };

    const createUpdatePropChamber =
        (key: keyof Chamber) =>
        (chamberIndex: number, value = 0) => {
            updateChamber(chamberIndex, key, value);
        };

    const save = async () => {
        setIsSaveLoading(true);
        setError(null);

        try {
            await saveChambers(deviceId, chambers);

            onClose();
        } catch (e) {
            ConsoleDev.error('Failed save chambers: ', e);

            setError('The server received a 2000P error indicating that the new state of the chambers was not saved successfully.');
        }

        setIsSaveLoading(false);
    };

    const uploadFileClick = () => {
        inputRef.current?.click();
    };

    const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.currentTarget.files;

        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];

        await Api.devices()
            .readChambersFile(deviceId, file)
            .onSuccess((res) => {
                const rawChambers = res.data.chambers;

                setChambers((prevChambers) =>
                    prevChambers.map((chamber) => {
                        const updated = rawChambers.find((rawChamber) => rawChamber.index === chamber.index);

                        if (!updated) {
                            return chamber;
                        }

                        return {
                            ...chamber,
                            init_value: updated.init_value,
                            spent_value: updated.spent_value,
                            product_udi: updated.product_udi,
                        };
                    })
                );
            });

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    return (
        <ModalInfo>
            <div className={S.Root}>
                <h1 className={S.RootTitle}>Chambers</h1>

                {error && <p className={S.error}>{error}</p>}

                <div className={S.ChambersEdit}>
                    {chambers.map((chamber, index) => (
                        <ChamberCardEdit
                            key={chamber.index}
                            chamber={chamber}
                            onChangeInitValue={createUpdatePropChamber('init_value')}
                            onChangeSpentValue={createUpdatePropChamber('spent_value')}
                            onChangeProductUdi={createUpdatePropChamber('product_udi')}
                        />
                    ))}
                </div>

                <div className={S.actions}>
                    <Button disabled={isSaveLoading} color="brown" className={S.EditButton} onClick={save}>
                        Save chambers
                    </Button>
                    <Button className={S.EditButton} onClick={onClose}>
                        Cancel edit
                    </Button>
                    <Button color="brown" className={S.EditButton} onClick={uploadFileClick}>
                        Upload File
                    </Button>
                    <input type="file" className={S.input} accept=".json,application/json" ref={inputRef} onChange={handleInputChange} />
                </div>
            </div>
        </ModalInfo>
    );
};
